// useCouponStore.js
// Store Pinia para o fluxo de Coupons.
// Espelha o padrão de useActivityGeneralStore.js:
//   – filtros client-side espelhados em backend (debounce + refetch)
//   – filteredCoupons como getter para filtragem visual instantânea
//   – setFilter dispara fetchCouponsPaged voltando à pág. 1

import { defineStore } from "pinia";
import * as couponService from "@/services/couponService";

function hasFilterValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function toBooleanFilter(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return "";
}

export const useCouponStore = defineStore("coupon", {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  state: () => ({
    coupons: [],
    selectedCouponIds: [],
    selectedCoupon: null,
    error: null,

    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    shouldForcePageOne: false,

    // Filtros enviados ao backend (e espelhados no painel visual)
    filters: {
      search: "", // busca textual por code
      status: "", // "" | true | false
      eligibleRule: "", // "" | "TARGETED" | ... (backend define enum)
      applyToAllCycles: "", // "" | true | false
    },
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // GETTERS
  // ─────────────────────────────────────────────────────────────────────────
  getters: {
    isAllSelected: (state) => (total) =>
      state.selectedCouponIds.length === total,

    /**
     * Filtragem client-side idêntica ao backend.
     * Serve de espelho visual instantâneo enquanto o debounce está em voo,
     * evitando "piscar" na tabela. Quando o backend responde, coupons já
     * chega filtrada e os dois conjuntos convergem.
     */
    filteredCoupons: (state) => {
      const f = state.filters;

      return state.coupons.filter((c) => {
        // ── Busca textual: apenas por código ─────────────────────────────
        if (f.search.trim() !== "") {
          const term = f.search.trim().toLowerCase();
          const code = (c.code ?? "").toLowerCase();
          if (!code.includes(term)) return false;
        }

        // ── Status (ativo/inativo) ───────────────────────────────────────
        if (hasFilterValue(f.status)) {
          const wantActive = toBooleanFilter(f.status);
          if (wantActive !== "" && Boolean(c.isActive) !== wantActive) {
            return false;
          }
        }

        // ── Regra de elegibilidade (match exato, case-insensitive) ───────
        if (f.eligibleRule.trim() !== "") {
          const rule = (c.eligibleRule ?? "").toLowerCase();
          if (rule !== f.eligibleRule.trim().toLowerCase()) return false;
        }

        // ── Ciclos (apply_to_all_cycles) ─────────────────────────────────
        if (hasFilterValue(f.applyToAllCycles)) {
          const wantAllCycles = toBooleanFilter(f.applyToAllCycles);
          if (
            wantAllCycles !== "" &&
            Boolean(c.applyToAllCycles) !== wantAllCycles
          ) {
            return false;
          }
        }

        return true;
      });
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────
  actions: {
    // ── Seleção ──────────────────────────────────────────────────────────
    toggleSelection(id) {
      const index = this.selectedCouponIds.indexOf(id);
      if (index > -1) {
        this.selectedCouponIds.splice(index, 1);
      } else {
        this.selectedCouponIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedCouponIds = [...ids];
    },

    clearSelection() {
      this.selectedCouponIds = [];
    },

    // ── Fetch paginado ────────────────────────────────────────────────────
    /**
     * Busca coupons paginados no backend com os filtros atuais.
     * @param {number} pageNumber
     * @param {number} pageSize
     */
    async fetchCouponsPaged(pageNumber, pageSize) {
      try {
        const filters = { ...this.filters };

        const response = await couponService.fetchCouponsPaged(
          pageNumber,
          pageSize,
          filters,
        );

        // Aceita tanto { items } quanto { data } no payload, para alinhar
        // com variações de contrato no backend.
        const payload = response.data ?? {};
        this.coupons = payload.items ?? payload.data ?? [];
        this.totalCount = payload.totalCount ?? 0;
        this.totalPages = payload.totalPages ?? 1;
        this.currentPage = pageNumber;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = { message };
        console.error("❌ Erro ao buscar coupons paginados:", message);
        throw new Error(message);
      }
    },

    // ── setFilter ─────────────────────────────────────────────────────────
    /**
     * Atualiza um filtro e dispara refetch voltando à página 1.
     * Chamado pelos watchers do SearchCouponInput / dropdowns com debounce.
     * @param {string} field  - chave de this.filters
     * @param {string|boolean} value
     */
    setFilter(field, value) {
      if (!(field in this.filters)) return;

      // ✅ PATCH: status e ciclos agora preservam boolean true/false.
      if (field === "status" || field === "applyToAllCycles") {
        this.filters[field] = toBooleanFilter(value);
      } else {
        this.filters[field] = value;
      }

      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchCouponsPaged(1, this.pageSize);
    },

    // ── clearFilters ──────────────────────────────────────────────────────
    /**
     * Limpa todos os filtros e (opcionalmente) dispara refetch completo.
     */
    clearFilters(shouldRefetch = true) {
      this.filters = {
        search: "",
        status: "",
        eligibleRule: "",
        applyToAllCycles: "",
      };

      this.currentPage = 1;
      this.shouldForcePageOne = true;

      if (shouldRefetch) {
        this.fetchCouponsPaged(1, this.pageSize);
      }
    },

    // ── Busca por ID ──────────────────────────────────────────────────────
    async getCouponById(id) {
      try {
        const response = await couponService.getCouponById(id);
        this.selectedCoupon = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar coupon por ID:", message);
        throw new Error(message);
      }
    },

    // ── Criação ───────────────────────────────────────────────────────────
    async createCoupon(data) {
      try {
        const response = await couponService.createCoupon(data);
        this.shouldForcePageOne = true;
        await this.fetchCouponsPaged(1, this.pageSize);
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao criar coupon:", message);
        throw new Error(message);
      }
    },

    // ── Atualização ───────────────────────────────────────────────────────
    async updateCoupon(data) {
      try {
        await couponService.updateCoupon(data);
        await this.fetchCouponsPaged(this.currentPage, this.pageSize);
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar coupon:", message);
        throw new Error(message);
      }
    },

    // ── Delete unitário ───────────────────────────────────────────────────
    async deleteCouponById(id) {
      try {
        await couponService.deleteCoupon(id);
        await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        // Se a página ficou vazia, regride uma página
        if (this.coupons.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupon:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote (seleção da store) ────────────────────────────────
    async deleteSelectedCoupons() {
      if (!this.selectedCouponIds.length) return;

      try {
        await couponService.deleteCouponsBatch(this.selectedCouponIds);
        this.clearSelection();
        await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        if (this.coupons.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupons em lote:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote por IDs externos ───────────────────────────────────
    async deleteCouponsByIds(ids) {
      if (!ids || !ids.length) return;
      try {
        await couponService.deleteCouponsBatch(ids);
        this.selectedCouponIds = this.selectedCouponIds.filter(
          (id) => !ids.includes(id),
        );
        await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        if (this.coupons.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponsPaged(this.currentPage, this.pageSize);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupons em lote (by ids):", message);
        throw new Error(message);
      }
    },

    // ── Toggle de status (ativo/inativo) ─────────────────────────────────
    async updateCouponStatus({ couponId, isActive }) {
      try {
        await couponService.updateCouponStatus(couponId, isActive);

        // Atualiza o estado local para a UI refletir a mudança sem recarregar
        const index = this.coupons.findIndex((c) => c.couponId === couponId);
        if (index !== -1) {
          this.coupons[index].isActive = isActive;
        }

        if (this.selectedCoupon && this.selectedCoupon.couponId === couponId) {
          this.selectedCoupon.isActive = isActive;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar o status do coupon:", message);
        throw new Error(message);
      }
    },
  },
});
